import { Fragment, useState } from 'react';
import { AnalysisDetailItem, AnalysisItem } from './data';
import { s } from './ResultReport.styles';
import styled from 'styled-components';
import { usePatternContext } from '../../../../../context/PatternContext';
import { useNavigate } from 'react-router-dom';
import PieChartGraph from './PieChartGraph';
import BarChartGraph from './BarChartGraph';

interface Props {
  analysisDetail: AnalysisDetailItem[];
  analysis: AnalysisItem[];
}

const ResultDetail = ({ analysisDetail, analysis }: Props) => {
  const [isAppraisal, setIsAppraisal] = useState(true);

  const formatValue: Record<number, { color: string; text: string }> = {
    0: { color: '#e5e8ea', text: '데이터없음' },
    1: { color: '#EC0010', text: '매우 낮음' },
    2: { color: '#EC5717', text: '낮음' },
    3: { color: '#27A131', text: '보통' },
    4: { color: '#008AFF', text: '높음' },
    5: { color: '#008AFF', text: '매우 높음' },
  };

  const ProfitChartData = (item: AnalysisDetailItem) => {
    return [
      {
        name: '시세 (기준)',
        value: isAppraisal
          ? Number(item?.market_prc_rate_std_appraisal_prc ?? 0)
          : Number(item?.market_prc_rate_std_prc ?? 0),
        color: '#484F55',
      },
      {
        name: '감정가',
        value: isAppraisal
          ? Number(item?.appraisal_prc_rate_std_appraisal_prc ?? 0)
          : Number(item?.appraisal_prc_rate_std_prc ?? 0),
        color: '#D6DBE0',
      },
      {
        name: '낙찰가',
        value: isAppraisal
          ? Number(item?.auction_prc_rate_std_appraisal_prc ?? 0)
          : Number(item.auction_prc_rate_std_prc ?? 0),
        color: '#008AFF',
      },
      {
        name: '매각가 (차익률)',
        value: isAppraisal
          ? Number(item?.transaction_prc_rate_std_appraisal_prc ?? 0)
          : Number(item.transaction_prc_rate_std_prc ?? 0),
        color: '#F45551',
      },
    ];
  };

  const profitabilityChartData = (item: AnalysisDetailItem) => {
    const investmentSaleRate = Number(item.investment_sale_rate) ?? 0;
    const auctionRate = Number(item.total_investment_sale_rate) ?? 0;
    const transactionRate = Number(item.auction_prc_sale_rate) ?? 0;

    return [
      {
        name: '실제 투자금 대비 매각수익률',
        value: investmentSaleRate < 0 ? 1 : investmentSaleRate, // 차트용 값
        displayValue: investmentSaleRate, // 실제 값
        color: '#008AFF',
      },
      {
        name: '전체 투자금 대비 매각수익률',
        value: auctionRate < 0 ? 1 : auctionRate,
        displayValue: auctionRate,
        color: '#484F55',
      },
      {
        name: '낙찰가 대비 매각수익률',
        value: transactionRate < 0 ? 1 : transactionRate,
        displayValue: transactionRate,
        color: '#F45551',
      },
    ];
  };

  const calculatePrice = (price: any | null) => {
    if (price === null || isNaN(Number(price))) return '-';

    const value = Number(price);
    const isNegative = value < 0; // 음수 여부 체크
    const absValue = Math.abs(value); // 절대값으로 계산

    let result = '';

    if (absValue >= 100_000_000) {
      const eok = (absValue / 100_000_000).toFixed(1); // 소수점 1자리
      result = `${parseFloat(eok)}억 원`;
    } else if (absValue >= 10_000) {
      const man = (absValue / 10_000).toFixed(0);
      result = `${parseFloat(man)}만 원`;
    } else {
      result = `${absValue.toLocaleString()}원`;
    }

    return isNegative ? `-${result}` : result;
  };

  return (
    <s.Section $isLast>
      <s.SectionHeader>
        <s.SectionTitle>상세분석</s.SectionTitle>
        <TabWrapper>
          <Tab $isMy>내 알고리즘</Tab>
          <Tab>전국</Tab>
        </TabWrapper>
      </s.SectionHeader>
      <Inner>
        <Section>
          <Title>알고리즘 모수</Title>
          <DataWrapper>
            {analysisDetail.map((item, idx) => (
              <Fragment key={idx}>
                <PieChartGraph
                  totalCnt={item?.total_cnt ?? 0}
                  successCnt={item?.success_cnt ?? 0}
                />
              </Fragment>
            ))}
          </DataWrapper>
        </Section>

        <Section>
          <Title>
            안전성
            <Grade $color={formatValue[analysis[0]?.safety ?? 0].color}>
              {formatValue[analysis[0]?.safety ?? 0].text}
            </Grade>
          </Title>
          <DataWrapper>
            {analysisDetail.map((item, idx) => (
              <s.DataColumn key={idx}>
                <s.DataItem>
                  <s.LegendColor $bg="#008AFF" />
                  <s.ItemContent>
                    <s.ItemLabel>수익 가능성</s.ItemLabel>
                    <s.ItemValue>{item?.success_rate ?? 0}%</s.ItemValue>
                  </s.ItemContent>
                </s.DataItem>
                <s.DataItem>
                  <s.LegendColor $bg="#D2D8DF" />
                  <s.ItemContent>
                    <s.ItemLabel>손실 가능성</s.ItemLabel>
                    <s.ItemValue>
                      {(100 - (item?.success_rate ?? 0)).toFixed(1)}%
                    </s.ItemValue>
                  </s.ItemContent>
                </s.DataItem>
              </s.DataColumn>
            ))}
          </DataWrapper>
        </Section>

        <Section>
          <PossibleTitleWrapper>
            <PossibleTitle>
              시세차익가능성
              <Grade
                $color={formatValue[analysis[0]?.profit_possibility ?? 0].color}
              >
                {formatValue[analysis[0]?.profit_possibility ?? 0].text}
              </Grade>
            </PossibleTitle>
            <ChangeButton onClick={() => setIsAppraisal((prev) => !prev)}>
              <img src="/assets/images/icon/refresh.svg" alt="" />
              <div>
                <span>{isAppraisal ? '시세' : '감정가'}</span>기준
              </div>
            </ChangeButton>
          </PossibleTitleWrapper>
          <DataWrapper>
            {analysisDetail.map((item, idx) => (
              <Fragment key={idx}>
                <BarChartGraph chartData={ProfitChartData(item)} />
              </Fragment>
            ))}
          </DataWrapper>
          <Extra>
            <ExtraTitle>보조지표</ExtraTitle>
            <DataWrapper>
              {analysisDetail.map((item, idx) => (
                <s.DataColumn key={idx}>
                  <s.ItemContent>
                    <s.ItemLabel $extra>낙찰가 평균</s.ItemLabel>
                    <s.ItemValue $extra>
                      {calculatePrice(item?.auction_prc ?? 0)}
                    </s.ItemValue>
                  </s.ItemContent>
                  <s.ItemContent>
                    <s.ItemLabel $extra>매각가 평균</s.ItemLabel>
                    <s.ItemValue $extra>
                      {calculatePrice(item?.transaction_prc ?? 0)}
                    </s.ItemValue>
                  </s.ItemContent>
                </s.DataColumn>
              ))}
            </DataWrapper>
          </Extra>
        </Section>

        <Section>
          <Title>
            수익성
            <Grade $color={formatValue[analysis[0]?.profitability ?? 0].color}>
              {formatValue[analysis[0]?.profitability ?? 0].text}
            </Grade>
          </Title>
          <DataWrapper>
            {analysisDetail.map((item, idx) => (
              <Fragment key={idx}>
                <BarChartGraph chartData={profitabilityChartData(item)} />
              </Fragment>
            ))}
          </DataWrapper>
          <Extra>
            <ExtraTitle>보조지표</ExtraTitle>
            <DataWrapper>
              {analysisDetail.map((item, idx) => (
                <s.DataColumn key={idx}>
                  <s.ItemContent>
                    <s.ItemLabel $extra>실제 투자금 평균</s.ItemLabel>
                    <s.ItemValue $extra>
                      {calculatePrice(item?.investment ?? 0)}
                    </s.ItemValue>
                  </s.ItemContent>
                  <s.ItemContent>
                    <s.ItemLabel $extra>전체 투자금 평균</s.ItemLabel>
                    <s.ItemValue $extra>
                      {calculatePrice(item?.total_investment ?? 0)}
                    </s.ItemValue>
                  </s.ItemContent>
                  <s.ItemContent>
                    <s.ItemLabel $extra>세후이익 평균</s.ItemLabel>
                    <s.ItemValue $extra>
                      {calculatePrice(item?.profit ?? 0)}
                    </s.ItemValue>
                  </s.ItemContent>
                  <s.ItemContent>
                    <s.ItemLabel $extra>낙찰가 평균</s.ItemLabel>
                    <s.ItemValue $extra>
                      {calculatePrice(item?.auction_prc ?? 0)}
                    </s.ItemValue>
                  </s.ItemContent>
                </s.DataColumn>
              ))}
            </DataWrapper>
          </Extra>
        </Section>
      </Inner>
    </s.Section>
  );
};

export default ResultDetail;

const TabWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

const Tab = styled.div<{ $isMy?: boolean }>`
  display: flex;
  padding: 8px 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-bottom: 2px solid;
  border-color: ${({ $isMy }) => ($isMy ? '#008AFF' : '#E1E2E4')};

  color: ${({ $isMy }) => ($isMy ? '#008AFF' : '#616161')};
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  align-self: stretch;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const Title = styled.div`
  display: flex;
  padding-bottom: 8px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-bottom: 1px solid #e1e2e4;

  color: #222;
  font-size: 16px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const PossibleTitleWrapper = styled.div`
  display: flex;
  padding-bottom: 8px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-bottom: 1px solid var(--semantic-line-solid-neutral, #e1e2e4);
`;
const PossibleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;

  color: var(--semantic-label-normal, #222);

  /* Re_SPOQA/body/bold_16 */
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 23.2px */
  letter-spacing: -0.32px;
`;

const ChangeButton = styled.div`
  display: flex;
  flex-direction: row;
  padding: 4px 16px;
  align-items: center;
  gap: 8px;
  border-radius: 9999px;
  background: var(--component-fill-normal, rgba(112, 115, 124, 0.08));
  cursor: pointer;

  color: var(--semantic-label-alternative, rgba(55, 56, 60, 0.61));

  /* Re_SPOQA/body/med_14 */
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;

  & > div {
    display: flex;
    flex-direction: row;
    gap: 2px;
  }

  span {
    color: var(--semantic-label-normal, #222);

    /* Re_SPOQA/body/med_14 */
    font-family: 'Spoqa Han Sans Neo';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 145%; /* 20.3px */
    letter-spacing: -0.28px;
  }
`;

const Grade = styled.div<{ $color?: string }>`
  color: ${({ $color }) => ($color ? $color : '#222')};
  font-size: 14px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const DataWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

const Extra = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 16px;
  background: #f7f8fc;
`;

const ExtraTitle = styled.div`
  display: flex;
  padding-bottom: 8px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-bottom: 1px solid #d4d7df;
  color: #222;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;
